import React, { useEffect, useState, useContext } from "react";
import FormControl from "@material-ui/core/FormControl";
import { useFormContext } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import { isEmpty } from "lodash-es";

import { convertBlobToFile } from "../../../utils";
import UploadImage from "../../../assets/icons/upload.svg";
import CustomPreview from "./CustomPreview";
import { DocumentContext } from "../../../contexts/document";
import {
  DocumentUploadError,
  InvalidFileTypeError
} from "../../../contexts/document/errors";

const useStyles = makeStyles((theme) => ({
  "@global": {
    "@keyframes load": {
      "0%": {
        opacity: 0.5,
        transform: "translateX(-100%)"
      },
      "100%": {
        opacity: 0.5,
        transform: "translateX(0%)"
      }
    },
    "@keyframes finish": {
      "0%": {
        opacity: 0.5,
        transform: "translateX(-10%)"
      },
      "90%": {
        opacity: 0.5,
        transform: "translateX(0%)"
      }
    }
  },
  formControl: {
    minWidth: "100%",
    "& .dzu-dropzone": {
      width: "100%",
      overflow: "auto",
      marginBottom: "1.5rem",
      background: theme.palette.background.grey,
      "& .dzu-inputLabel": {
        padding: theme.spacing(3),
        borderRadius: "50px"
      },
      "& .dzu-inputLabelWithFiles": {
        padding: theme.spacing(3),
        width: "100%"
      },
      "& .dzu-inputLabel, .dzu-inputLabelWithFiles": {
        flexGrow: "1",
        color: theme.palette.border.dark,
        fontWeight: "400",
        textAlign: "center",
        whiteSpace: "pre",
        margin: "auto",
        fontSize: "1.1rem",
        lineHeight: "1.7rem"
      },
      [theme.breakpoints.up("sm")]: {
        marginTop: theme.spacing(2),
        border: "2px dashed #c2c2c2",
        minHeight: "12rem",
        "& .dzu-inputLabelWithFiles": {
          height: "0%",
          backgroundColor: theme.palette.background.grey
        },
        "& .dzu-inputLabel": {
          display: "inline-grid",
          "&:before": {
            content: "url(" + UploadImage + ")",
            transform: "scale(1.5)",
            marginBottom: theme.spacing(2),
            padding: "1rem"
          }
        },
        ".dzu-inputLabelWithFiles": {
          "&:before": {
            content: "none"
          }
        }
      },
      [theme.breakpoints.down("xs")]: {
        border: "none",
        minHeight: "9rem",
        overflow: "auto",
        background: theme.palette.background.grey,
        "& .dzu-inputLabel": {
          marginTop: "6rem !important",
          "&:before": {
            marginTop: "-6rem !important",
            content: "url(" + UploadImage + ")",
            transform: "scale(1.2)",
            padding: "1.4rem"
          }
        },
        "& .dzu-inputLabel, .dzu-inputLabelWithFiles": {
          marginTop: "2rem",
          color: theme.palette.text.secondary,
          height: "auto",
          background: theme.palette.button.main,
          fontWeight: theme.typography.fontWeightBold,
          flexDirection: "column",
          maxHeight: "20px",
          borderRadius: "5px",
          width: "80%",
          margin: "auto"
        }
      }
    }
  },
  error: {
    color: theme.palette.error.main,
    fontSize: "1rem"
  }
}));

const UploadField = ({ name, validations }) => {
  const classes = useStyles();
  const { setValue, register, unregister, errors, setError, clearErrors } =
    useFormContext();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [existingFiles, setExistingFiles] = useState([]);
  const {
    documents,
    errors: documentErrors,
    dispatch
  } = useContext(DocumentContext);

  const mobileButtonText = "Foto oder Datei hinzufügen";

  const uploadText = isDesktop
    ? "Dokument hierher ziehen oder \nKlicken um Datei hochzuladen"
    : mobileButtonText;

  useEffect(() => {
    register(name, validations);

    return () => {
      unregister(name, validations);
    };
  }, [name, validations, register, unregister]);

  // Initialise component with existing files
  useEffect(() => {
    const convertedFiles = documents.map(convertBlobToFile);
    setExistingFiles(convertedFiles);
    // eslint-disable-next-line
  }, []);

  // Cleanup
  useEffect(() => {
    return function cleanup() {
      const errorsToClear = [
        DocumentUploadError.type,
        InvalidFileTypeError.type
      ];

      errorsToClear.forEach((type) =>
        dispatch({ type: "clearError", payload: { type } })
      );

      dispatch({ type: "clearSize" });
    };
  }, [dispatch]);

  // Set form values from context
  useEffect(() => {
    setValue(name, !isEmpty(documents));
  }, [documents, name, setValue]);

  // Set form errors from context
  useEffect(() => {
    if (isEmpty(documentErrors)) {
      return clearErrors();
    }

    Object.entries(documentErrors).forEach(([type, message]) => {
      setError(type, {
        type: "manual",
        message
      });
    });
  }, [documentErrors, name, setError, clearErrors]);

  const handleChangeStatus = ({ meta, file }, status) => {
    if (status.includes("rejected") || status.includes("error"))
      return handleErrorStatus(status);
    if (status === "done") return handleDoneStatus({ meta, file });
    if (status === "removed") return handleRemovedStatus(meta);
  };

  const handleErrorStatus = (status) => {
    const error = new DocumentUploadError(status);

    dispatch({
      type: "setError",
      payload: { type: error.constructor.type, message: error.message }
    });
  };

  const handleDoneStatus = ({ meta, file }) => {
    const shouldAddFile =
      documents.length === 0 ||
      documents.some(({ name, size }) => {
        return file.name !== name && file.size !== size;
      });

    if (shouldAddFile) {
      dispatch({
        type: "add",
        payload: { name: meta.name, value: file, size: meta.size }
      });
    }
  };

  const handleRemovedStatus = (meta) => {
    dispatch({
      type: "remove",
      payload: { name: meta.name, size: meta.size }
    });
  };

  return (
    <FormControl className={classes.formControl}>
      <Dropzone
        onChangeStatus={handleChangeStatus}
        PreviewComponent={CustomPreview}
        accept=".jpeg, .jpg, .png, .tiff, .doc, .docx, .xls, .xlsx, .pdf"
        inputContent={uploadText}
        inputWithFilesContent={!isDesktop && mobileButtonText}
        initialFiles={existingFiles}
      />
      {Object.entries(errors).map(([key, error]) => {
        return (
          <div className={classes.error} key={key}>
            {error.message}
          </div>
        );
      })}
    </FormControl>
  );
};

export default UploadField;
